import * as React from "react";
import { Typography, Button, DialogTitle, Dialog, TextField, Alert, FormControl, RadioGroup, Radio, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { apiBulkUpdateGraderWorkitems, apiBatchAddCoderNotes } from "../../../utils/api";
import { SnackbarContext } from "../../../context/AlertsContext";
import { WorkitemStatus } from "../ProjectSelectColumn/types";

type Props = {
    selected: any[];
    open: boolean;
    handleClose: () => void;
    handleAfterRevert: () => void;
    isQualityAssessment?: boolean;
    cqaWorkitemStatus?: string;
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    }
}));

const StyledButton = styled(Button)({
    borderRadius: "4px",
    textTransform: "none",
    flexGrow: 1,
    maxWidth: '100px',
    maxHeight: '40px',
    minWidth: '100px',
    minHeight: '40px'
});

async function saveRevertComment(
    comment: string,
    selected,
    revertTarget: WorkitemStatus
): Promise<boolean> {
    const trimmedComment = comment.trim();
    if (trimmedComment === "") return true;

    const notes = selected.map((item: any) => {
        const newStatus =
            item.qa_status === WorkitemStatus.CQA_APPROVED
                ? revertTarget
                : WorkitemStatus.IN_CQA;
        return {
            note_content: trimmedComment,
            lookup_reference: item.sd_case_id || item.case_id,
            lookup_type: "CQA_FEEDBACK_COMMENT",
            event_data: {
                old_status: item.qa_status,
                new_status: newStatus,
            },
        };
    });

    try {
        await apiBatchAddCoderNotes(notes);
        return true;
    } catch (noteErr) {
        console.error("Error saving revert comment:", noteErr);
        return false;
    }
}

export const RevertApproval = ({
    selected,
    open,
    handleClose,
    handleAfterRevert,
    isQualityAssessment,
    cqaWorkitemStatus,
    ...props
}: Props) => {
    const MIN_COMMENT_WORDS = 2;

    const { openSnackbar } = React.useContext(SnackbarContext);
    const [isReverting, setIsReverting] = React.useState(false);
    const [comment, setComment] = React.useState("");
    const [revertTarget, setRevertTarget] = React.useState<WorkitemStatus>(WorkitemStatus.CQA_COMPLETE);

    const wordCount = comment.trim() === "" ? 0 : comment.trim().split(/\s+/).length;
    const commentIsInvalid = comment.trim() !== "" && wordCount < MIN_COMMENT_WORDS;

    const handleDialogClose = () => {
        setComment("");
        setRevertTarget(WorkitemStatus.CQA_COMPLETE);
        handleClose();
    };

    const handleRevertClick = async () => {
        if (!selected || selected.length === 0 || commentIsInvalid) return;

        // Build the updates array for the API call
        const updates = selected.map((item: any) => ({
            sdCaseId: item.sd_case_id || item.case_id, // Handle both possible field names
            status: item.qa_status === WorkitemStatus.CQA_APPROVED ? revertTarget : WorkitemStatus.IN_CQA
        }));

        setIsReverting(true);

        try {
            await apiBulkUpdateGraderWorkitems(updates);

            const noteSaved = await saveRevertComment(comment, selected, revertTarget);

            openSnackbar(
                `${selected.length} work item${selected.length > 1 ? "s" : ""} ${ noteSaved ? "successfully reverted to Complete status." : "reverted, but comment failed to save." }`,
                noteSaved ? "success" : "warning"
            );

            handleAfterRevert();
            handleDialogClose();
        } catch (err) {
            console.error("Error reverting work items:", err);
            openSnackbar(
                `Failed to revert work items. Please try again.`,
                "error"
            );
        } finally {
            setIsReverting(false);
        }
    };

    return (
        <StyledDialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ m: 0 }} id="revert-approval-title">
                {cqaWorkitemStatus === WorkitemStatus.CQA_COMPLETE ? "Revert Submission" : "Revert Approval"}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
                disabled={isReverting}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <Alert severity={ !selected || selected?.length === 0 ? "warning" : "info"} sx={{ mb: 2 }}>
                    {selected ? selected.length : 0} work item{selected && selected.length !== 1 ? "s" : ""} selected
                </Alert>
                {cqaWorkitemStatus === WorkitemStatus.CQA_APPROVED && (
                    <FormControl sx={{ mb: 2 }}>
                        <RadioGroup
                            row
                            name="revertTargetRadioGroup"
                            value={revertTarget}
                            onChange={(e) => setRevertTarget(e.target.value as WorkitemStatus)}
                            sx={{ gap: "24px" }}
                        >
                            <FormControlLabel
                                value={WorkitemStatus.CQA_COMPLETE}
                                control={<Radio />}
                                label="Back to complete"
                            />
                            <FormControlLabel
                                value={WorkitemStatus.IN_CQA}
                                control={<Radio />}
                                label="Back to in progress"
                            />
                        </RadioGroup>
                    </FormControl>
                )}
                <Typography gutterBottom sx={{ mb: 2 }}>
                    {(() => {
                        const approved = selected?.filter((i: any) => i.qa_status === WorkitemStatus.CQA_APPROVED) ?? [];
                        const complete = selected?.filter((i: any) => i.qa_status === WorkitemStatus.CQA_COMPLETE) ?? [];
                        return (
                            <>
                                {approved.length > 0 && (
                                    <>
                                        You are about to revert {approved.length} <strong>approved</strong> work item{approved.length === 1 ? "" : "s"} back to <strong>{revertTarget === WorkitemStatus.IN_CQA ? "in progress" : "complete"}</strong> status.
                                        {revertTarget === WorkitemStatus.IN_CQA && " This will allow coders to edit them again before resubmission."}
                                    </>
                                )}
                                {approved.length > 0 && complete.length > 0 && <><br /><br /></>}
                                {complete.length > 0 && (
                                    <>
                                        You are about to revert {complete.length} <strong>complete</strong> work item{complete.length === 1 ? "" : "s"} back to <strong>in progress</strong> status.
                                        This will allow coders to edit them again before resubmission.
                                    </>
                                )}
                            </>
                        );
                    })()}
                </Typography>
                <TextField
                    label="Comment (optional)"
                    multiline
                    rows={3}
                    fullWidth
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={isReverting}
                    error={commentIsInvalid}
                    helperText={commentIsInvalid ? `Please enter at least ${MIN_COMMENT_WORDS} words (${wordCount}/${MIN_COMMENT_WORDS})` : undefined}
                />
                <Typography>
                    Are you sure you want to proceed?
                </Typography>
            </DialogContent>
            <DialogActions>
                <StyledButton
                    variant="outlined"
                    onClick={() => handleDialogClose()}
                    disabled={isReverting}
                >
                    Cancel
                </StyledButton>
                <StyledButton
                    variant="contained"
                    disabled={!selected || selected.length === 0 || isReverting || commentIsInvalid}
                    sx={{ ml: 5 }}
                    onClick={handleRevertClick}
                >
                    {isReverting ? "Reverting..." : "Revert"}
                </StyledButton>
            </DialogActions>
        </StyledDialog>
    );
};